/**
 * Market Sentiment System - Configuration
 * Background market simulation affecting CPS/click multiplier
 */

// === Price Simulation Parameters ===
export const MARKET_CONFIG = {
  // Price range
  startPrice: 100,
  minPrice: 10,
  maxPrice: 300,

  // Random walk parameters
  momentumDecay: 0.92,
  noiseScale: 2.5,
  meanReversionStrength: 0.003,
  meanReversionTarget: 100,

  // Tick rate (ms between price updates)
  tickInterval: 2000,

  // Price history length for chart
  priceHistoryLength: 60,

  // Multiplier mapping: price -> earningsMultiplier
  // minPrice(10) -> 0.3x, maxPrice(300) -> 2.0x
  multiplierMin: 0.3,
  multiplierMax: 2.0,

  // News system
  newsMinInterval: 30_000,   // 30s
  newsMaxInterval: 90_000,   // 90s
  newsDuration: 20_000,      // 20s effect duration

  // Special events (rare)
  specialEventChance: 0.03,  // 3% per news cycle
  crashPrice: 15,
  boomPrice: 280,
  specialDuration: 15_000,   // 15s forced state
} as const;

// === News Events ===
export interface NewsEvent {
  id: string;
  impact: number;      // price momentum push (-15 to +15)
  sentiment: 'positive' | 'negative' | 'neutral';
}

export const MARKET_NEWS: NewsEvent[] = [
  // Positive news
  { id: 'news_celeb_tweet', impact: 8, sentiment: 'positive' },
  { id: 'news_nova_update', impact: 6, sentiment: 'positive' },
  { id: 'news_whale_buy', impact: 10, sentiment: 'positive' },
  { id: 'news_bolt_partner', impact: 7, sentiment: 'positive' },
  { id: 'news_star_listing', impact: 9, sentiment: 'positive' },
  { id: 'news_bull_market', impact: 12, sentiment: 'positive' },

  // Negative news
  { id: 'news_hack', impact: -10, sentiment: 'negative' },
  { id: 'news_regulation', impact: -8, sentiment: 'negative' },
  { id: 'news_bear_market', impact: -12, sentiment: 'negative' },
  { id: 'news_frog_burn', impact: -5, sentiment: 'negative' },
];

// === Player Manipulation Actions ===
export interface ManipulationConfig {
  id: string;
  costMultiplier: number;  // cost = getCurrentCPS(state) * costMultiplier, min floor
  minCost: number;         // minimum cost floor
  duration: number;        // ms
  impact: number;          // momentum push
  backfireChance: number;  // 0-1, chance of opposite effect
  cooldown: number;        // ms
}

/**
 * v2.0: manipulations target an exchange coin. The legacy global (earnings
 * multiplier) push is kept at 50% strength so old-player feel is preserved.
 */
export const GLOBAL_MANIP_SCALE = 0.5;

/** Coin momentum push per tick = impact * this * coin.volatility * remainingFraction */
export const MANIP_COIN_IMPACT_SCALE = 0.18;

/** One-shot negative momentum (in volatility units) on the target coin when pump_dump ends */
export const PUMP_DUMP_COIN_CRASH = 4;

export const MANIPULATIONS: ManipulationConfig[] = [
  {
    id: 'sns_hype',
    costMultiplier: 30,      // 30s of CPS
    minCost: 100,
    duration: 15_000,
    impact: 8,
    backfireChance: 0.3,     // 30% backfire
    cooldown: 20_000,
  },
  {
    id: 'influencer',
    costMultiplier: 300,     // 5 min of CPS
    minCost: 5000,
    duration: 30_000,
    impact: 15,
    backfireChance: 0.05,    // 5% backfire (reliable)
    cooldown: 60_000,
  },
  {
    id: 'pump_dump',
    costMultiplier: 1800,    // 30 min of CPS
    minCost: 50000,
    duration: 20_000,
    impact: 25,              // huge push up...
    backfireChance: 0,       // no backfire, BUT guaranteed crash after
    cooldown: 180_000,       // 3 min cooldown
    // Special: after duration ends, price crashes to ~40% of current
  },
];
